import React from 'react';
import { Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import { CREATING, FINISHED, PAUSED, PLAYING } from 'state';

type Props = {
  sessionStatus: number;
  canWrite: boolean;
  fetching: boolean;
  fetched: boolean;
  scopeMatches: boolean;
  loadFailed: boolean;
  preview?: boolean;
  retry: () => void;
};

/** Explain disabled participation controls without exposing assessment internals. */
const CaptureStatusNotice: React.FC<Props> = ({
  sessionStatus,
  canWrite,
  fetching,
  fetched,
  scopeMatches,
  loadFailed,
  preview = false,
  retry,
}) => {
  if (preview) return null;

  let message: string;
  let retryable = false;
  let loading = false;
  if (sessionStatus === PAUSED) {
    message =
      'Session paused — your trainer must resume it before you can assign news or change conviction.';
  } else if (sessionStatus === FINISHED) {
    message =
      'Session finished — saved news assignments and convictions are read-only.';
  } else if (sessionStatus === CREATING) {
    message =
      'The session has not started — news assignments and conviction controls become available when it starts.';
  } else if (sessionStatus !== PLAYING) {
    message =
      'The session is not running — news assignments and conviction controls are read-only until it resumes.';
  } else if (loadFailed) {
    message = canWrite
      ? 'Your saved views could not be refreshed. You can retry without leaving the session.'
      : 'Voting controls could not be loaded. Retry to restore news assignments and conviction controls.';
    retryable = true;
  } else if (canWrite) {
    return null;
  } else if (fetching || !fetched || !scopeMatches) {
    message = 'Loading news assignments and conviction controls…';
    loading = true;
    // An interrupted request should not strand a session on a loading message.
    retryable = !fetching;
  } else {
    message = 'Voting controls are not available yet. Retry to check again.';
    retryable = true;
  }

  return (
    <div className="markets-v4-capture-status" role="status" aria-live="polite">
      <span>
        {loading && <LoadingOutlined aria-hidden />} {message}
      </span>
      {retryable && (
        <Button
          size="small"
          onClick={retry}
          loading={fetching}
          disabled={fetching}
        >
          Retry
        </Button>
      )}
    </div>
  );
};

export default CaptureStatusNotice;
